function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import { useCallback, useState, useRef } from 'react';
import { useDebounceCallback } from '@react-hook/debounce';
import { callAllHandlers } from '../../../utils';
import { useControllableProp } from '../../../hooks';
/**
 * Hook that gives a text input and action button a
 * search type of functionality.
 *
 * Calls onSearch callback after x number of character entered.
 * Text Input can also be debounced as not to call onSearch callback
 * too many times in quick succession.
 */
export var useSearch = function useSearch() {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var {
    searchOnCharacterCount = 3,
    value: valueProp,
    searchOnEnter = true,
    defaultValue,
    debounceMillis = 350,
    onChange,
    onSearch: onSearchProp = () => {}
  } = props;
  var [valueState, setValue] = useState(defaultValue || '');
  var [isControlled, value] = useControllableProp(valueProp, valueState);
  var onSearchDebounced = useDebounceCallback(onSearchProp, debounceMillis);
  var isBackspacePressed = useRef(false);
  var canSearch = useCallback(value => {
    if (!searchOnCharacterCount) return true;
    return searchOnCharacterCount && value.length >= searchOnCharacterCount;
  }, [searchOnCharacterCount]);
  var handleChange = useCallback(event => {
    if (!isControlled) {
      setValue(event.target.value);
    }
    onChange === null || onChange === void 0 ? void 0 : onChange(event);
    if (isBackspacePressed.current) return;
    if (canSearch(event.target.value)) {
      onSearchDebounced === null || onSearchDebounced === void 0 ? void 0 : onSearchDebounced(event.target.value);
    }
  }, [isControlled, onChange, canSearch, onSearchDebounced]);
  var onKeyUp = useCallback(event => {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      isBackspacePressed.current = false;
      if (canSearch(value)) {
        onSearchDebounced === null || onSearchDebounced === void 0 ? void 0 : onSearchDebounced(value);
      }
    }
  }, [canSearch, onSearchDebounced, value]);
  var onKeyDown = useCallback(event => {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      isBackspacePressed.current = true;
    }
    if (value.length > 0 && searchOnEnter && event.key === 'Enter') {
      onSearchProp === null || onSearchProp === void 0 ? void 0 : onSearchProp(value);
    }
  }, [onSearchProp, searchOnEnter, value]);

  // Properties that will be spread on text input used as part of
  // search.
  var getInputProps = useCallback(function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return _objectSpread(_objectSpread({}, props), {}, {
      value,
      onKeyUp: callAllHandlers(props.onKeyUp, onKeyUp),
      onKeyDown: callAllHandlers(props.onKeyDown, onKeyDown),
      onChange: callAllHandlers(props.onChange, handleChange)
    });
  }, [onKeyDown, onKeyUp, handleChange, value]);

  // Properties that will be spread on button used as part of
  // search.
  var getActionProps = useCallback(function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return _objectSpread(_objectSpread({}, props), {}, {
      onClick: callAllHandlers(props.onClick, () => onSearchProp === null || onSearchProp === void 0 ? void 0 : onSearchProp(value))
    });
  }, [onSearchProp, value]);
  return {
    getInputProps,
    getActionProps
  };
};